<script>
  import { createEventDispatcher, onMount } from 'svelte'
  import {
    getExerciseLibrary,
    getExercisesByMuscleGroup,
    saveExerciseToLibrary,
    deleteExerciseFromLibrary,
    getMuscleGroupName,
    getExerciseDisplayName
  } from '../services/db.js'

  const dispatch = createEventDispatcher()

  let exercises = []
  let isLoading = true
  let searchQuery = ''
  let selectedMuscleGroup = null
  let showForm = false
  let editingExercise = null

  let formData = {
    id: '',
    name: '',
    muscleGroup: '',
    description: ''
  }

  const muscleGroups = [
    { value: null, label: '全部' },
    { value: 'chest', label: '胸' },
    { value: 'back', label: '背' },
    { value: 'shoulders', label: '肩' },
    { value: 'arms', label: '手臂' },
    { value: 'legs', label: '腿' },
    { value: 'core', label: '核心' },
    { value: 'cardio', label: '有氧' },
    { value: 'other', label: '其他' }
  ]

  const groupEmojis = {
    chest: '🫁', back: '🔙', shoulders: '🦾', arms: '💪',
    legs: '🦵', core: '🎯', cardio: '❤️', other: '📋'
  }

  const groupOrder = ['chest', 'back', 'shoulders', 'arms', 'legs', 'core', 'cardio', 'other']

  onMount(async () => {
    await loadExercises()
  })

  async function loadExercises() {
    isLoading = true
    try {
      exercises = await getExerciseLibrary()
    } catch (error) {
      console.error('Failed to load exercises:', error)
      alert('載入運動項目失敗')
    } finally {
      isLoading = false
    }
  }

  $: filteredExercises = exercises.filter(e => {
    const matchesSearch = !searchQuery ||
      e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (e.zhName && e.zhName.includes(searchQuery)) ||
      (e.description && e.description.toLowerCase().includes(searchQuery.toLowerCase()))
    const matchesGroup = !selectedMuscleGroup || e.muscleGroup === selectedMuscleGroup
    return matchesSearch && matchesGroup
  })

  $: groupedExercises = (() => {
    const grouped = {}
    filteredExercises.forEach(exercise => {
      const group = exercise.muscleGroup || 'other'
      if (!grouped[group]) grouped[group] = []
      grouped[group].push(exercise)
    })
    return grouped
  })()

  $: totalCount = exercises.length
  $: customCount = exercises.filter(e => e.isCustom).length

  function openAddForm() {
    editingExercise = null
    formData = { id: '', name: '', muscleGroup: '', description: '' }
    showForm = true
  }

  function openEditForm(exercise) {
    editingExercise = exercise
    formData = {
      id: exercise.id,
      name: exercise.name,
      muscleGroup: exercise.muscleGroup,
      description: exercise.description || ''
    }
    showForm = true
  }

  function closeForm() {
    showForm = false
    editingExercise = null
  }

  async function handleSave() {
    if (!formData.name.trim()) {
      alert('請輸入運動項目名稱')
      return
    }
    if (!formData.muscleGroup) {
      alert('請選擇肌群')
      return
    }

    // Check for duplicates
    const duplicate = exercises.find(e =>
      e.name.toLowerCase() === formData.name.toLowerCase() &&
      e.id !== formData.id
    )
    if (duplicate) {
      alert('已存在相同名稱的運動項目')
      return
    }

    try {
      const exerciseData = {
        name: formData.name.trim(),
        muscleGroup: formData.muscleGroup,
        description: formData.description.trim()
      }

      if (formData.id) {
        exerciseData.id = formData.id
        exerciseData.createdAt = editingExercise?.createdAt
      }

      await saveExerciseToLibrary(exerciseData)
      showToast(formData.id ? '運動項目已更新' : '運動項目已新增')
      await loadExercises()
      closeForm()
    } catch (error) {
      console.error('Save failed:', error)
      alert('儲存失敗，請稍後再試')
    }
  }

  async function handleDelete(exercise) {
    if (!confirm(`確定要刪除「${exercise.name}」嗎？\n\n此操作無法復原。`)) {
      return
    }

    try {
      await deleteExerciseFromLibrary(exercise.id)
      showToast('運動項目已刪除')
      await loadExercises()
    } catch (error) {
      console.error('Delete failed:', error)
      showToast('刪除失敗，請稍後再試', 'error')
    }
  }

  function clearFilters() {
    searchQuery = ''
    selectedMuscleGroup = null
  }

  function showToast(message, type = 'success') {
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500'
    const toast = document.createElement('div')
    toast.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in`
    toast.textContent = message
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 2000)
  }
</script>

<div class="exercise-manager-container fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
  <div class="max-w-4xl mx-auto px-4 py-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800">運動項目管理</h1>
        <p class="text-sm text-gray-600 mt-1">共 {totalCount} 個項目（{customCount} 個自訂）</p>
      </div>
      <button
        type="button"
        class="text-gray-600 hover:text-gray-800 text-2xl font-bold"
        on:click={() => dispatch('close')}
      >
        ✕
      </button>
    </div>

    <!-- Search and Add -->
    <div class="card mb-6">
      <div class="flex flex-col md:flex-row gap-4">
        <div class="flex-1">
          <label class="block text-sm font-medium text-gray-700 mb-1">搜尋運動項目</label>
          <input
            type="text"
            class="input-field w-full"
            placeholder="輸入名稱或描述..."
            bind:value={searchQuery}
          />
        </div>
        <div class="flex items-end">
          <button type="button" class="btn btn-primary whitespace-nowrap" on:click={openAddForm}>
            + 新增項目
          </button>
        </div>
      </div>

      <!-- Muscle Group Filter -->
      <div class="mt-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">按肌群篩選</label>
        <div class="flex flex-wrap gap-2">
          {#each muscleGroups as group}
            <button
              type="button"
              class="filter-type-btn"
              class:active={selectedMuscleGroup === group.value}
              on:click={() => selectedMuscleGroup = group.value}
            >
              {group.label}
            </button>
          {/each}
        </div>
      </div>
    </div>

    <!-- Add/Edit Form -->
    {#if showForm}
      <div class="card mb-6">
        <h2 class="text-lg font-semibold mb-4 text-gray-800">
          {editingExercise ? '編輯運動項目' : '新增運動項目'}
        </h2>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              名稱 <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              class="input-field w-full"
              placeholder="例如：槓鈴深蹲"
              bind:value={formData.name}
              maxlength="50"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              肌群 <span class="text-red-500">*</span>
            </label>
            <select class="input-field w-full" bind:value={formData.muscleGroup}>
              <option value="">請選擇肌群</option>
              <option value="chest">胸</option>
              <option value="back">背</option>
              <option value="shoulders">肩</option>
              <option value="arms">手臂</option>
              <option value="legs">腿</option>
              <option value="core">核心</option>
              <option value="cardio">有氧</option>
              <option value="other">其他</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">描述（選填）</label>
            <input
              type="text"
              class="input-field w-full"
              placeholder="例如：Barbell Squat"
              bind:value={formData.description}
              maxlength="100"
            />
          </div>

          <div class="flex gap-2">
            <button type="button" class="btn btn-primary flex-1" on:click={handleSave}>儲存</button>
            <button type="button" class="btn btn-secondary flex-1" on:click={closeForm}>取消</button>
          </div>
        </div>
      </div>
    {/if}

    <!-- Exercise List -->
    <div class="space-y-6">
      {#if isLoading}
        <div class="text-center py-12">
          <div class="spinner mx-auto mb-4"></div>
          <p class="text-gray-600">載入中...</p>
        </div>
      {:else if filteredExercises.length === 0}
        <div class="card text-center py-12">
          {#if searchQuery || selectedMuscleGroup}
            <div class="text-6xl mb-4">🔍</div>
            <h3 class="text-xl font-semibold text-gray-800 mb-2">找不到符合條件的項目</h3>
            <p class="text-gray-600 mb-4">試試調整搜尋條件或篩選器</p>
            <button type="button" class="btn btn-secondary" on:click={clearFilters}>清除篩選</button>
          {:else}
            <div class="text-6xl mb-4">📝</div>
            <h3 class="text-xl font-semibold text-gray-800 mb-2">尚無運動項目</h3>
            <p class="text-gray-600">點擊上方按鈕新增第一個項目</p>
          {/if}
        </div>
      {:else}
        {#each groupOrder as group}
          {#if groupedExercises[group] && groupedExercises[group].length > 0}
            <div class="card">
              <h3 class="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <span>{groupEmojis[group] || '📋'}</span>
                <span>{getMuscleGroupName(group)}</span>
                <span class="text-sm font-normal text-gray-500">({groupedExercises[group].length})</span>
              </h3>

              <div class="space-y-2">
                {#each groupedExercises[group] as exercise}
                  <div class="exercise-item flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div class="flex-1">
                      <div class="font-medium text-gray-800">{getExerciseDisplayName(exercise)}</div>
                      {#if exercise.description}
                        <div class="text-sm text-gray-500">{exercise.description}</div>
                      {/if}
                      {#if exercise.isCustom}
                        <span class="inline-block text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded mt-1">自訂</span>
                      {/if}
                    </div>

                    {#if exercise.isCustom}
                      <div class="flex gap-2 ml-4">
                        <button
                          type="button"
                          class="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          on:click={() => openEditForm(exercise)}
                        >
                          編輯
                        </button>
                        <button
                          type="button"
                          class="text-red-600 hover:text-red-800 text-sm font-medium"
                          on:click={() => handleDelete(exercise)}
                        >
                          刪除
                        </button>
                      </div>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          {/if}
        {/each}
      {/if}
    </div>
  </div>
</div>
